import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { getEmpresaAtual } from './api';
import {
  coresHex, hexParaRgb, escurecer, clarear,
} from '../styles/styleUtils';

// Converte um hex de coresHex em rgb() 0-1 do pdf-lib (que não entende var()/hex).
const rgbHex = (hex) => {
  const { r, g, b } = hexParaRgb(hex);
  return rgb(r / 255, g / 255, b / 255);
};

const generatePdfRelatorioCustosServico = async (data, filtro) => {
  if (!data) return;

  const pdfDoc  = await PDFDocument.create();
  const fontReg = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold= await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  const W = 595.28; const H = 841.89;
  const ML = 40; const MR = 40;
  const CW = W - ML - MR;

  const C = {
    cinzaChumbo: rgbHex(coresHex.chumbo),
    cinzaMedio:  rgbHex(coresHex.cinzaMedio),
    cinzaClaro:  rgbHex(coresHex.cinzaClaro),
    laranja:     rgbHex(coresHex.laranja),
    laranjaClaro:rgbHex(coresHex.laranjaClaro),
    branco:      rgb(1, 1, 1),
    preto:       rgb(0.133, 0.133, 0.133),
    borda:       rgbHex(coresHex.borda),
    verde:       rgb(0.180, 0.490, 0.196),
    vermelho:    rgb(0.776, 0.157, 0.157),
  };

  const CORES_HEX = [coresHex.laranja, coresHex.cinzaMedio];

  const fmtBRL = (v) => parseFloat(v||0).toLocaleString('pt-BR', { style:'currency', currency:'BRL' });

  const drawRect  = (pg, x, y, w, h, color) => pg.drawRectangle({ x, y, width:w, height:h, color });
  const drawBRect = (pg, x, y, w, h, fill, border) => pg.drawRectangle({ x, y, width:w, height:h, color:fill, borderColor:border||C.borda, borderWidth:0.5 });
  const drawText  = (pg, t, x, y, s, f, color) => pg.drawText(String(t??''), { x, y, size:s, font:f, color });
  const drawLine  = (pg, x1, y1, x2, y2, color=C.borda, t=0.5) => pg.drawLine({ start:{x:x1,y:y1}, end:{x:x2,y:y2}, thickness:t, color });

  const tH = 14;
  const drawRow = (pg, cells, y) => {
    cells.forEach((c) => {
      drawBRect(pg, c.x, y-tH+2, c.w, tH, c.fill, c.bc||C.borda);
      if (c.text != null && c.text !== '') {
        const sz=c.sz||8; const fn=c.fn||fontReg;
        let tx = c.x+3;
        if (c.al==='r') tx = c.x+c.w-fn.widthOfTextAtSize(String(c.text),sz)-3;
        if (c.al==='c') tx = c.x+(c.w-fn.widthOfTextAtSize(String(c.text),sz))/2;
        drawText(pg, c.text, tx, y-tH+5, sz, fn, c.cl||C.preto);
      }
    });
    return y-tH;
  };

  // ── Logo ──────────────────────────────────────────────────────────────────
  let logoImage = null;
  let empresa = null;
  try {
    empresa = await getEmpresaAtual();
    if (empresa?.logoBranca) {
      const base64 = empresa.logoBranca.split(',')[1];
      const bin = atob(base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
      logoImage = await pdfDoc.embedPng(bytes);
    }
  } catch (e) { /* sem logo */ }

  const usuarioLogado = JSON.parse(localStorage.getItem('userData') || '{}');
  const solicitante   = usuarioLogado?.nome || '—';

  const periodo = [
    filtro?.datainicial ? new Date(filtro.datainicial).toLocaleDateString('pt-BR') : '',
    filtro?.datafinal   ? new Date(filtro.datafinal).toLocaleDateString('pt-BR')   : '',
  ].filter(Boolean).join(' até ');

  const TITULO = 'Relatório de Custos por Serviço';
  const drawHeader = (pg, titulo) => {
    drawRect(pg, 0, H-44, W, 44, C.cinzaChumbo);
    drawRect(pg, 0, H-44, 6, 44, C.laranja);
    if (logoImage) {
      const d = logoImage.scaleToFit(70, 28);
      pg.drawImage(logoImage, { x:ML, y:H-39, width:d.width, height:d.height });
    } else {
      drawText(pg, empresa?.nome || 'Marcki Engenharia', ML, H - 26, 11, fontBold, C.branco);
    }
    drawText(pg, titulo, W-MR-fontBold.widthOfTextAtSize(titulo,10), H-24, 10, fontBold, C.branco);
    if (periodo) drawText(pg, `Período: ${periodo}`, W-MR-fontReg.widthOfTextAtSize(`Período: ${periodo}`,8), H-36, 8, fontReg, rgb(0.8,0.8,0.8));
    return H-58;
  };

  const drawFooter = (pg) => {
    drawRect(pg, 0, 0, W, 26, C.cinzaChumbo);
    drawRect(pg, 0, 0, 6, 26, C.laranja);
    drawText(pg, `Solicitante: ${solicitante}`, ML+8, 9, 7, fontReg, rgb(0.8,0.8,0.8));
    drawText(pg, `Gerado em ${new Date().toLocaleDateString('pt-BR')}`, W-MR-70, 9, 7, fontReg, rgb(0.8,0.8,0.8));
  };

  const r = data.resumo || {};
  const tabela = data.tabela || [];

  // ── Gráfico de barras agrupadas (Venda x Custo) via Canvas ────────────────
  const gerarBarrasAgrupadasCanvas = (dados, dispW, dispH) => new Promise((resolve) => {
    const SCALE=3;
    const cW=Math.round(dispW*SCALE);
    const cH=Math.round(dispH*SCALE);
    const cv=document.createElement('canvas');
    cv.width=cW; cv.height=cH;
    const ctx=cv.getContext('2d');

    const padX=10*SCALE; const padTop=18*SCALE; const padBot=34*SCALE;
    const baseY=cH-padBot; const chartH=baseY-padTop;

    const maxV=Math.max(...dados.map(d=>Math.max(d.venda,d.custo)),1);
    const n=dados.length;
    const slot=(cW-padX*2)/n;
    const barW=Math.min(slot*0.35,40*SCALE);
    const gap=4*SCALE;

    ctx.fillStyle='#F5F5F5'; ctx.fillRect(0,0,cW,cH);
    for(let g=0;g<=4;g+=1){
      const gy=baseY-(g/4)*chartH;
      ctx.strokeStyle='#DDD'; ctx.lineWidth=1*SCALE;
      ctx.beginPath(); ctx.moveTo(padX,gy); ctx.lineTo(cW-padX,gy); ctx.stroke();
    }

    const quebraRotulo=(texto,maxW)=>{
      const palavras=String(texto).split(' ');
      const linhas=[]; let atual='';
      palavras.forEach(p=>{
        const teste=atual?`${atual} ${p}`:p;
        if(ctx.measureText(teste).width<=maxW||!atual){ atual=teste; } else { linhas.push(atual); atual=p; }
      });
      if(atual) linhas.push(atual);
      const out=linhas.slice(0,2);
      if(linhas.length>2){
        let ult=out[1];
        while(ult.length>0&&ctx.measureText(`${ult}...`).width>maxW){ ult=ult.slice(0,-1); }
        out[1]=`${ult}...`;
      }
      return out.map(ln=>{
        if(ctx.measureText(ln).width<=maxW) return ln;
        let s=ln;
        while(s.length>0&&ctx.measureText(`${s}...`).width>maxW){ s=s.slice(0,-1); }
        return `${s}...`;
      });
    };

    ctx.textAlign='center';
    dados.forEach((d,i)=>{
      const slotX=padX+i*slot;
      const barHVenda=(d.venda/maxV)*chartH;
      const barHCusto=(d.custo/maxV)*chartH;
      const xVenda=slotX+(slot-barW*2-gap)/2;
      const xCusto=xVenda+barW+gap;

      ctx.fillStyle=CORES_HEX[0];
      ctx.beginPath(); ctx.roundRect(xVenda,baseY-barHVenda,barW,barHVenda,Math.min(4*SCALE,barW/2)); ctx.fill();
      ctx.fillStyle=CORES_HEX[1];
      ctx.beginPath(); ctx.roundRect(xCusto,baseY-barHCusto,barW,barHCusto,Math.min(4*SCALE,barW/2)); ctx.fill();

      ctx.font=`${11*SCALE}px Arial`; ctx.fillStyle='#555';
      const linhaH=13*SCALE;
      const linhas=quebraRotulo(d.name,slot-6*SCALE);
      linhas.forEach((ln,li)=>{
        ctx.fillText(ln,slotX+slot/2,baseY+14*SCALE+li*linhaH);
      });
    });
    resolve(cv.toDataURL('image/png'));
  });

  const dadosVendaCusto = tabela.filter(e=>e.quantidade>0).map(e=>({name:e.etapa, venda:e.valorVenda, custo:e.custo}));
  let barrasImg = null;
  if (dadosVendaCusto.length > 0) {
    const url = await gerarBarrasAgrupadasCanvas(dadosVendaCusto, CW, 200);
    const bytes = await fetch(url).then(resp => resp.arrayBuffer());
    barrasImg = await pdfDoc.embedPng(bytes);
  }

  // ============================================================
  // PG 1 — KPIs + GRÁFICO
  // ============================================================
  const p1 = pdfDoc.addPage([W, H]);
  let y1 = drawHeader(p1, TITULO);
  y1 -= 10;

  if (periodo) { drawText(p1, `De ${periodo}`, ML, y1, 8, fontReg, C.cinzaMedio); y1 -= 14; }
  if (filtro?.servico) { drawText(p1, `Serviço: ${filtro.servico}`, ML, y1, 8, fontReg, C.laranja); y1 -= 14; }
  if (filtro?.responsavel) { drawText(p1, `Engenheiro: ${filtro.responsavel.nome}`, ML, y1, 8, fontReg, C.laranja); y1 -= 14; }

  // KPIs
  const kpiW = (CW-15)/4; const kpiH = 36;
  const margemColor = (r.margemTotal||0) >= 0 ? C.verde : C.vermelho;
  const kpis = [
    { label: 'Total Venda (R$)',  valor: fmtBRL(r.totalVenda) },
    { label: 'Total Custo (R$)',  valor: fmtBRL(r.totalCusto) },
    { label: 'Margem Total (R$)', valor: fmtBRL(r.margemTotal), cor: margemColor },
    { label: 'Margem (%)',        valor: `${Number(r.margemPercentual||0).toFixed(1)}%`, cor: margemColor },
  ];
  kpis.forEach((kpi, i) => {
    const x = ML + i*(kpiW+5);
    drawBRect(p1, x, y1-kpiH, kpiW, kpiH, C.laranjaClaro, C.laranja);
    drawRect(p1, x, y1-kpiH, 3, kpiH, kpi.cor||C.laranja);
    drawText(p1, kpi.label, x+6, y1-13, 7, fontReg, C.cinzaMedio);
    drawText(p1, kpi.valor, x+6, y1-28, 10, fontBold, kpi.cor||C.cinzaChumbo);
  });
  y1 -= kpiH+20;

  // Legenda simples
  drawRect(p1, ML, y1-8, 8, 8, rgbHex(coresHex.laranja));
  drawText(p1, 'Venda', ML+12, y1-6, 7, fontReg, C.preto);
  drawRect(p1, ML+60, y1-8, 8, 8, rgbHex(coresHex.cinzaMedio));
  drawText(p1, 'Custo', ML+72, y1-6, 7, fontReg, C.preto);
  y1 -= 16;

  if (barrasImg) {
    drawText(p1, 'Venda vs Custo por Etapa', ML, y1, 9, fontBold, C.preto);
    y1 -= 12;
    p1.drawImage(barrasImg, { x:ML, y:y1-200, width:CW, height:200 });
    y1 -= 210;
  }

  drawFooter(p1);

  // ============================================================
  // PG 2 — TABELA POR ETAPA
  // ============================================================
  const p2 = pdfDoc.addPage([W, H]);
  let y2 = drawHeader(p2, TITULO);
  y2 -= 16;

  drawRect(p2, ML, y2+2, CW, 16, C.cinzaChumbo);
  drawText(p2, 'CUSTOS POR ETAPA', ML+6, y2+7, 8, fontBold, C.branco);
  y2 -= 16;

  const c1=CW*0.32; const c2=CW*0.12; const c3=CW*0.19; const c4=CW*0.19; const c5=CW-c1-c2-c3-c4;
  y2 = drawRow(p2, [
    {x:ML,             w:c1, fill:C.laranja, text:'Etapa',      fn:fontBold, cl:C.branco},
    {x:ML+c1,          w:c2, fill:C.laranja, text:'Qtd',        fn:fontBold, cl:C.branco, al:'c'},
    {x:ML+c1+c2,       w:c3, fill:C.laranja, text:'Venda (R$)', fn:fontBold, cl:C.branco, al:'r'},
    {x:ML+c1+c2+c3,    w:c4, fill:C.laranja, text:'Custo (R$)', fn:fontBold, cl:C.branco, al:'r'},
    {x:ML+c1+c2+c3+c4, w:c5, fill:C.laranja, text:'Margem (R$)',fn:fontBold, cl:C.branco, al:'r'},
  ], y2);

  tabela.forEach((e, i) => {
    if (y2 < 50) return;
    const fill = i%2===0 ? C.cinzaClaro : C.branco;
    y2 = drawRow(p2, [
      {x:ML,             w:c1, fill, text:e.etapa},
      {x:ML+c1,          w:c2, fill, text:String(e.quantidade), al:'c'},
      {x:ML+c1+c2,       w:c3, fill, text:fmtBRL(e.valorVenda), al:'r'},
      {x:ML+c1+c2+c3,    w:c4, fill, text:fmtBRL(e.custo), al:'r'},
      {x:ML+c1+c2+c3+c4, w:c5, fill, text:fmtBRL(e.margem), al:'r', cl:e.margem>=0?C.verde:C.vermelho},
    ], y2);
  });

  drawLine(p2, ML, y2-8, W-MR, y2-8, C.laranja, 0.5);
  drawText(p2, `Margem Total: ${fmtBRL(r.margemTotal)} (${Number(r.margemPercentual||0).toFixed(1)}%)`, ML, y2-18, 7, fontReg, C.cinzaMedio);

  drawFooter(p2);

  // ============================================================
  // PG 3 — SEGMENTAÇÕES
  // ============================================================
  const p3 = pdfDoc.addPage([W, H]);
  let y3 = drawHeader(p3, TITULO);
  y3 -= 16;

  let pgAtual = p3;
  let yAtual  = y3;

  const drawSegTable = (titulo, grupos) => {
    if (!grupos || grupos.length === 0) return;
    if (yAtual < 100) {
      drawFooter(pgAtual);
      pgAtual = pdfDoc.addPage([W, H]);
      yAtual  = drawHeader(pgAtual, TITULO);
      yAtual -= 16;
    }
    drawRect(pgAtual, ML, yAtual+2, CW, 16, C.cinzaChumbo);
    drawText(pgAtual, titulo, ML+6, yAtual+7, 8, fontBold, C.branco);
    yAtual -= 16;
    const sc1=CW*0.30; const sc2=CW*0.10; const sc3=CW*0.18; const sc4=CW*0.18; const sc5=CW*0.14; const sc6=CW-sc1-sc2-sc3-sc4-sc5;
    const header = () => drawRow(pgAtual, [
      {x:ML,                      w:sc1, fill:C.laranja, text:'Grupo',       fn:fontBold, cl:C.branco},
      {x:ML+sc1,                  w:sc2, fill:C.laranja, text:'Qtd',         fn:fontBold, cl:C.branco, al:'c'},
      {x:ML+sc1+sc2,              w:sc3, fill:C.laranja, text:'Venda (R$)',  fn:fontBold, cl:C.branco, al:'r'},
      {x:ML+sc1+sc2+sc3,          w:sc4, fill:C.laranja, text:'Custo (R$)',  fn:fontBold, cl:C.branco, al:'r'},
      {x:ML+sc1+sc2+sc3+sc4,      w:sc5, fill:C.laranja, text:'Margem (R$)', fn:fontBold, cl:C.branco, al:'r'},
      {x:ML+sc1+sc2+sc3+sc4+sc5,  w:sc6, fill:C.laranja, text:'Margem (%)',  fn:fontBold, cl:C.branco, al:'c'},
    ], yAtual);
    yAtual = header();
    grupos.forEach((g, i) => {
      if (yAtual < 50) {
        drawFooter(pgAtual);
        pgAtual = pdfDoc.addPage([W, H]);
        yAtual  = drawHeader(pgAtual, TITULO);
        yAtual -= 16;
        yAtual = header();
      }
      const fill = i%2===0 ? C.cinzaClaro : C.branco;
      yAtual = drawRow(pgAtual, [
        {x:ML,                     w:sc1, fill, text:(g.grupo||'—').substring(0,26)},
        {x:ML+sc1,                 w:sc2, fill, text:String(g.quantidade), al:'c'},
        {x:ML+sc1+sc2,             w:sc3, fill, text:fmtBRL(g.valorVenda), al:'r'},
        {x:ML+sc1+sc2+sc3,         w:sc4, fill, text:fmtBRL(g.custo), al:'r'},
        {x:ML+sc1+sc2+sc3+sc4,     w:sc5, fill, text:fmtBRL(g.margem), al:'r', cl:g.margem>=0?C.verde:C.vermelho},
        {x:ML+sc1+sc2+sc3+sc4+sc5, w:sc6, fill, text:`${Number(g.margemPercentual||0).toFixed(1)}%`, al:'c'},
      ], yAtual);
    });
    yAtual -= 14;
  };

  drawSegTable('SEGMENTAÇÃO POR TIPO DE SERVIÇO', data.porTipoServico);
  drawSegTable('SEGMENTAÇÃO POR ENGENHEIRO RESPONSÁVEL', data.porResponsavel);

  drawFooter(pgAtual);

  const pdfBytes = await pdfDoc.save();
  const blob     = new Blob([pdfBytes], { type:'application/pdf' });
  const url      = URL.createObjectURL(blob);
  const link     = document.createElement('a');
  link.href      = url;
  link.download  = 'relatorio-custos-servico.pdf';
  link.click();
  URL.revokeObjectURL(url);
};

export default generatePdfRelatorioCustosServico;